// ---------------------------------------------------------------------------
// storageHelpers — shared derivations for Storage v2 (sql/086, 2026-09-08).
//
// Model in one breath: FACILITIES hold UNITS (rooms BBM rents, fixed
// inventory); TENANCIES put a client — or a name-only tenant — into one
// or more of those units, or into a client-owned unit somewhere else.
// Everything below is pure; the pages compose these.
// ---------------------------------------------------------------------------

export const STORAGE_SIZES = ['10x10', '10x15', '10x25'];

export const unitLabel = (unit) => unit ? `${unit.unit_number}${unit.size ? ` · ${unit.size}` : ''}` : '';

// CLIENT RATE CARD for a unit: per-unit override, else the facility's
// size list. (Jaylen 9/8/26: the size list is what tenants are BILLED,
// not BBM's cost — BBM's cost is one flat lease on the whole block.)
export function unitRate(unit, facility) {
  if (!unit) return 0;
  if (unit.monthly_cost != null && unit.monthly_cost !== '') return parseFloat(unit.monthly_cost) || 0;
  const rates = facility?.size_rates || {};
  return parseFloat(rates[unit.size]) || 0;
}

// Rate-card total for a tenancy's units — the DEFAULT billed rate.
export function tenancyRateCard(tenancy, unitsById, facilitiesById) {
  if (!tenancy || tenancy.ownership !== 'bbm') return 0;
  const fac = facilitiesById?.[tenancy.facility_id];
  return (tenancy.unit_ids || []).reduce((s, id) => s + unitRate(unitsById?.[id], fac), 0);
}

// What BBM pays: flat lease per facility.
export const facilityLease = (facility) => parseFloat(facility?.monthly_lease_cost) || 0;

export const tenantName = (tenancy, clientsById) =>
  (tenancy.client_id && clientsById?.[tenancy.client_id]?.name) || tenancy.tenant_name || 'Unknown tenant';

// unitId → the non-closed tenancy occupying it (quoted counts as a hold).
export function buildOccupancy(tenancies) {
  const map = {};
  (tenancies || []).forEach(t => {
    if (t.status === 'closed') return;
    (t.unit_ids || []).forEach(id => { map[id] = t; });
  });
  return map;
}

// 'internal' | 'occupied' | 'quoted' | 'empty' | 'retired'
export function unitState(unit, occupancy) {
  if (!unit.active) return 'retired';
  if (unit.kind === 'internal') return 'internal';
  const t = occupancy[unit.id];
  if (!t) return 'empty';
  return t.status === 'quoted' ? 'quoted' : 'occupied';
}

export const UNIT_STATE_STYLE = {
  empty:    { label: 'Empty',     cls: 'bg-emerald-50 border-emerald-200 text-emerald-700',  dot: 'bg-emerald-500' },
  quoted:   { label: 'Quoted',    cls: 'bg-amber-50 border-amber-300 text-amber-800',        dot: 'bg-amber-500' },
  occupied: { label: 'Occupied',  cls: 'bg-indigo-50 border-indigo-300 text-indigo-800',     dot: 'bg-indigo-600' },
  internal: { label: 'BBM use',   cls: 'bg-slate-100 border-slate-300 text-slate-600',       dot: 'bg-slate-500' },
  retired:  { label: 'Retired',   cls: 'bg-white border-dashed border-slate-200 text-slate-400', dot: 'bg-slate-300' },
};

// Paid-thru aging. Returns null when no date, else { days, tone, label }.
// days > 0 = overdue by that many days; days <= 0 = paid ahead.
export function paidThruStatus(paidThru, today = new Date()) {
  if (!paidThru) return null;
  const d = new Date(paidThru + 'T00:00:00');
  if (isNaN(d)) return null;
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((t0 - d) / 86400000);
  if (days > 0)  return { days, tone: 'overdue', label: `${days}d past due` };
  if (days > -7) return { days, tone: 'soon',    label: days === 0 ? 'due today' : `due in ${-days}d` };
  return { days, tone: 'ok', label: `paid thru ${paidThru}` };
}

export const PAID_TONE_CLS = {
  overdue: 'bg-rose-100 text-rose-700 border-rose-200',
  soon:    'bg-amber-100 text-amber-700 border-amber-200',
  ok:      'bg-emerald-50 text-emerald-700 border-emerald-200',
};

// Address a tenancy resolves to (estimate origin/destination).
export function tenancyAddress(tenancy, unitsById, facilitiesById) {
  if (!tenancy) return '';
  const fac = facilitiesById?.[tenancy.facility_id];
  const units = (tenancy.unit_ids || []).map(id => unitsById?.[id]).filter(Boolean);
  if (fac) {
    const nums = units.map(u => u.unit_number).join(', ');
    return `${fac.address || fac.name}${nums ? ` — Unit${units.length > 1 ? 's' : ''} ${nums}` : ''}`;
  }
  return tenancy.facility_address || [tenancy.facility_name, tenancy.unit_number && `Unit ${tenancy.unit_number}`].filter(Boolean).join(' — ');
}

// Short "where" for cards/banners.
export function tenancyPlace(tenancy, unitsById, facilitiesById) {
  const fac = facilitiesById?.[tenancy.facility_id];
  const units = (tenancy.unit_ids || []).map(id => unitsById?.[id]).filter(Boolean);
  const nums = units.map(u => u.unit_number).join(', ');
  if (fac) return [fac.name, nums ? `Unit${units.length > 1 ? 's' : ''} ${nums}` : (tenancy.status === 'quoted' ? 'unit TBD' : '')].filter(Boolean).join(' · ');
  return [tenancy.facility_name, tenancy.unit_number && `Unit ${tenancy.unit_number}`].filter(Boolean).join(' · ') || 'Facility not set';
}

export const byId = (rows) => Object.fromEntries((rows || []).map(r => [r.id, r]));
